const pathSeparator = "//"; // Legacy - Can be any characters of any length, remember to change in backend too
const numberedPathSeparator = "."; // New numbered path separator (e.g., "001.002.010")
const serverURL =
  process.env.NODE_ENV === "production" ? "" : "http://localhost:4000";
const socketURL =
  process.env.NODE_ENV === "production" ? "" : "http://localhost:4000";

const postBorderColors = {
  ask: "#ffc7c7",
  tell: "#e0d2f7",
  "event-feeler": "#f9d7b8",
  commercial: "#b8f0f9",
  event: "#f9f4b8",
};

const currencies = [
  {
    name: "EUR",
    sign: "€",
  },
  {
    name: "USD",
    sign: "$",
  },
];

const urlRegex = /((https?:\/\/)?[^\s]+(\.[^\s]+)+)/g;

function splitText(text) {
  const segments = [];
  let match;

  let lastIndex = 0;
  while ((match = urlRegex.exec(text)) !== null) {
    const linkText = match[0];
    const linkIndex = match.index;

    if (linkIndex > lastIndex) {
      const nonLinkText = text.slice(lastIndex, linkIndex);
      segments.push({ text: nonLinkText, isLink: false });
    }

    segments.push({ text: linkText, isLink: true });
    lastIndex = linkIndex + linkText.length;
  }

  if (lastIndex < text.length) {
    const nonLinkText = text.slice(lastIndex);
    segments.push({ text: nonLinkText, isLink: false });
  }

  return segments.map((segment) => {
    if (segment.isLink) {
      segment.url = segment.text;
      if (!segment.url.startsWith("http")) {
        segment.url = `https://${segment.url}`;
      }
    }
    return segment;
  });
}

function getObjectByPath(obj, path) {
  let parent = obj;
  const properties = path.split(pathSeparator).filter(Boolean).slice(1);
  for (const property of properties) {
    const match = parent.children.find((child) => child.name === property);
    if (!match) {
      return null;
    }
    parent = match;
  }
  return parent;
}

// New numbered path helpers
function getNodeByNumberedPath(nodesIndex, numberedPath) {
  // If we have an index, use it directly
  if (
    nodesIndex &&
    typeof nodesIndex === "object" &&
    nodesIndex[numberedPath]
  ) {
    return nodesIndex[numberedPath];
  }
  // Fallback: treat nodesIndex as root node and traverse
  if (!numberedPath || numberedPath === "") return nodesIndex;

  const segments = numberedPath.split(numberedPathSeparator).filter(Boolean);
  let current = nodesIndex;

  for (const segment of segments) {
    if (!current || !current.children) return null;
    const child = current.children.find(
      (c) =>
        c.numberedPath ===
        segments
          .slice(0, segments.indexOf(segment) + 1)
          .join(numberedPathSeparator)
    );
    if (!child) return null;
    current = child;
  }
  return current;
}

function getParentNumberedPath(numberedPath) {
  if (!numberedPath || numberedPath === "") return null;
  const segments = numberedPath.split(numberedPathSeparator).filter(Boolean);
  if (segments.length <= 1) return ""; // Root has no parent
  return segments.slice(0, -1).join(numberedPathSeparator);
}

function joinNumberedPath(prefix, segment) {
  if (!prefix || prefix === "") return segment;
  return `${prefix}${numberedPathSeparator}${segment}`;
}

function buildBreadcrumbFromNumberedPath(nodesIndex, numberedPath) {
  // Returns array of {name, numberedPath} for breadcrumb display
  if (!numberedPath || numberedPath === "") {
    const rootNode = nodesIndex[""] || nodesIndex;
    return rootNode && rootNode.name
      ? [{ name: rootNode.name, numberedPath: "" }]
      : [];
  }

  const segments = numberedPath.split(numberedPathSeparator).filter(Boolean);
  const breadcrumbs = [];

  // Add root
  const rootNode = nodesIndex[""] || nodesIndex;
  if (rootNode && rootNode.name) {
    breadcrumbs.push({ name: rootNode.name, numberedPath: "" });
  }

  // Add each level
  let currentPath = "";
  for (const segment of segments) {
    currentPath = currentPath
      ? `${currentPath}${numberedPathSeparator}${segment}`
      : segment;
    const node = nodesIndex[currentPath];
    if (node && node.name) {
      breadcrumbs.push({ name: node.name, numberedPath: currentPath });
    }
  }

  return breadcrumbs;
}

function buildNodeIndex(rootNode) {
  // Build a flat index: { numberedPath: node, _id: node }
  const index = {};

  function traverse(node) {
    if (!node) return;

    // Normalize: backend uses 'path' for numbered path, frontend uses 'numberedPath'
    // Copy path to numberedPath if numberedPath doesn't exist
    if (node.numberedPath === undefined && node.path !== undefined) {
      // Check if path is a numbered path (e.g., "001.002") not a legacy path (e.g., "//Tech")
      if (node.path && !node.path.startsWith(pathSeparator)) {
        node.numberedPath = node.path;
      }
    }

    // Index by numberedPath
    if (node.numberedPath !== undefined) {
      index[node.numberedPath] = node;
    } else if (node.numberedPath === undefined && !node.parent) {
      // Root node might have empty numberedPath
      index[""] = node;
    }

    // Index by _id
    if (node._id) {
      index[`_id_${node._id}`] = node;
    }

    // Traverse children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  traverse(rootNode);
  return index;
}

/**
 * Build a reverse index mapping slug → node for fast URL resolution
 * @param {Object} rootNode - Root node of the tree
 * @returns {Object} slugIndex - Maps "web-dev" → node object
 */
function buildSlugIndex(rootNode) {
  const slugIndex = {};

  function traverse(node) {
    if (!node) return;

    // Create slug from node name
    if (node.name && (node.numberedPath || node.path)) {
      const slug = node.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, ""); // Remove special characters

      // Store the node reference
      slugIndex[slug] = node;
    }

    // Traverse children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  traverse(rootNode);
  return slugIndex;
}

function getNodeById(nodesIndex, id) {
  return nodesIndex[`_id_${id}`] || null;
}

function stringIncludesCaseInsensitive(string, searchSubstring) {
  return string.toLowerCase().includes(searchSubstring.toLowerCase());
}

function toFixedWithoutLeadingZeros(number) {
  const fixed = number.toFixed(1);
  return fixed.replace(/\.0$/, "");
}

function FormatNumber(number) {
  if (isNaN(number)) return Number(0);
  if (number < 1000) {
    return number;
  }
  if (number < 1000000) {
    return `${toFixedWithoutLeadingZeros(number / 1000)}k`;
  }
  if (number < 1000000000) {
    return `${toFixedWithoutLeadingZeros(number / 1000000)}M`;
  }
  return `${toFixedWithoutLeadingZeros(number / 1000000000)}B`;
}
function pathToFormattedDirectory(path, item) {
  // If item with name is provided, use that for display
  if (item?.name) {
    return item.name;
  }

  // Handle numbered path format (e.g., "001.002.010")
  if (path && path.includes(".")) {
    return path.split(".").filter(Boolean).slice(-3).join(" / ");
  }

  // Legacy name-based path fallback (e.g., "//Technology//Programming")
  return (
    path?.split(pathSeparator).filter(Boolean).slice(-3).join(" / ") ||
    "Unknown"
  );
}

function getUserFeedLink(path, id, nodesIndex) {
  // Convert numbered path to slug-based URL
  // If nodesIndex is provided, look up the node and use its name as slug
  if (nodesIndex && path) {
    const node = nodesIndex[path];
    if (node && node.name) {
      const slug = node.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      return `/${slug}?user=${id}`;
    }
  }

  // Fallback: if no nodesIndex or node not found, use "feed"
  return `/feed?user=${id}`;
}

const UtilsExport = {
  getObjectByPath,
  stringIncludesCaseInsensitive,
  formatNumber: FormatNumber,
  splitText,
  pathToFormattedDirectory,
  getUserFeedLink,
  urlRegex,
  pathSeparator,
  numberedPathSeparator,
  serverURL,
  socketURL,
  currencies,
  postBorderColors,
  // New numbered path helpers
  getNodeByNumberedPath,
  getParentNumberedPath,
  joinNumberedPath,
  buildBreadcrumbFromNumberedPath,
  buildNodeIndex,
  buildSlugIndex,
  getNodeById,
};

export default UtilsExport;
